import React, { Suspense, lazy, useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import AppLoader from './components/AppLoader';
import Auth from './pages/Auth';
import { supabase } from './services/supabaseClient';
import { getProfile, syncDailyLoginState } from './services/database';
import { syncInventoryCache } from './services/inventory';
import { syncFocusSessionsCache } from './services/focus';
import { logError, logInfo } from './services/logger';
import { getEffectiveRole, normalizeUserRole } from './services/tier-system';
import Landing from './pages/Landing';

const Home = lazy(() => import('./pages/Home'));
const Challenges = lazy(() => import('./pages/Challenges'));
const ChallengeDetail = lazy(() => import('./pages/ChallengeDetail'));
const CreateChallenge = lazy(() => import('./pages/CreateChallenge'));
const Ranking = lazy(() => import('./pages/Ranking'));
const Profile = lazy(() => import('./pages/Profile'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Settings = lazy(() => import('./pages/Settings'));
const About = lazy(() => import('./pages/About'));
const XPHistory = lazy(() => import('./pages/XPHistory'));
const Shop = lazy(() => import('./pages/Shop'));
const Friends = lazy(() => import('./pages/Friends'));
const DirectChat = lazy(() => import('./pages/DirectChat'));
const MessagesInbox = lazy(() => import('./pages/MessagesInbox'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Nexus = lazy(() => import('./pages/Nexus'));
const FocusMode = lazy(() => import('./pages/FocusMode'));
const SkillTree = lazy(() => import('./pages/SkillTree'));
const SystemLogs = lazy(() => import('./pages/SystemLogs'));
const Oracle = lazy(() => import('./pages/Oracle'));
const PvpDuel = lazy(() => import('./pages/PvpDuel'));
const Roulette = lazy(() => import('./pages/Roulette'));
const Admin = lazy(() => import('./pages/Admin'));
const VipThankYou = lazy(() => import('./pages/VipThankYou'));

const renderLazyPage = (page: React.ReactNode, title: string, subtitle: string) => (
  <Suspense fallback={<AppLoader title={title} subtitle={subtitle} />}>
    {page}
  </Suspense>
);

const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T | null> => {
  let timeoutId: number | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<null>((resolve) => {
        timeoutId = window.setTimeout(() => {
          logError('app', `Timeout ao executar etapa critica de boot: ${label}`);
          resolve(null);
        }, ms);
      })
    ]);
  } finally {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
  }
};

const syncBootStateInBackground = async (userId: string, source: 'initial-session' | 'auth-event') => {
  try {
    logInfo('app', 'Iniciando sincronizacao em segundo plano.', { source, userId });

    await Promise.allSettled([
      withTimeout(syncDailyLoginState(userId), 6000, 'syncDailyLoginState'),
      withTimeout(syncInventoryCache(userId), 6000, 'syncInventoryCache'),
      withTimeout(syncFocusSessionsCache(userId), 6000, 'syncFocusSessionsCache')
    ]);

    logInfo('app', 'Sincronizacao em segundo plano concluida.', { source, userId });
  } catch (error) {
    logError('app', 'Falha geral na sincronizacao em segundo plano.', error);
  }
};

const PublicEntryRoute = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    const readSession = async () => {
      const sessionResult = await withTimeout(supabase.auth.getSession(), 5000, 'supabase.auth.getSession.public');
      const session = sessionResult?.data?.session ?? null;

      setHasSession(Boolean(session?.user));
      setLoading(false);
    };

    void readSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setHasSession(Boolean(session?.user));
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return <AppLoader title="Preparando sua entrada" subtitle="Conferindo se existe sessao salva neste navegador." />;
  }

  if (hasSession) {
    return <Navigate to="/app/home" replace />;
  }

  return <>{children}</>;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getInitialSession = async () => {
      const sessionResult = await withTimeout(supabase.auth.getSession(), 5000, 'supabase.auth.getSession');
      const session = sessionResult?.data?.session ?? null;

      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user?.id) {
        void syncBootStateInBackground(session.user.id, 'initial-session');
      }
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user?.id) {
          void syncBootStateInBackground(session.user.id, 'auth-event');
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return <AppLoader title="Abrindo seu painel" subtitle="Validando sessao e preparando sua proxima run." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const validateAdminAccess = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setAllowed(false);
        setLoading(false);
        return;
      }

      const profile = await getProfile(session.user.id);
      const role = normalizeUserRole(profile);
      setAllowed(role === 'admin');
      setLoading(false);
    };

    validateAdminAccess();
  }, []);

  if (loading) {
    return <AppLoader title="Validando acesso admin" subtitle="Conferindo role e liberando o console certo." />;
  }

  if (!allowed) {
    return <Navigate to="/app/home" replace />;
  }

  return <>{children}</>;
};

const VipRoute = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const validateVipAccess = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setAllowed(false);
        setLoading(false);
        return;
      }

      const profile = await getProfile(session.user.id);
      const role = getEffectiveRole(profile, session.user.id);
      setAllowed(role === 'vip');
      setLoading(false);
    };

    void validateVipAccess();
  }, []);

  if (loading) {
    return <AppLoader title="Abrindo espaco VIP" subtitle="Validando o acesso especial dessa pagina." />;
  }

  if (!allowed) {
    return <Navigate to="/app/profile" replace />;
  }

  return <>{children}</>;
};

const App = () => {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<PublicEntryRoute><Landing /></PublicEntryRoute>} />
        <Route path="/login" element={<PublicEntryRoute><Auth initialMode="login" /></PublicEntryRoute>} />
        <Route path="/signup" element={<PublicEntryRoute><Auth initialMode="signup" /></PublicEntryRoute>} />
        <Route path="/reset-password" element={<Auth initialMode="login" />} />

        <Route path="/app/home" element={<ProtectedRoute><Layout>{renderLazyPage(<Home />, 'Abrindo a Home', 'Montando seu painel principal e o ritmo do dia.')}</Layout></ProtectedRoute>} />
        <Route path="/app/nexus" element={<ProtectedRoute><Layout>{renderLazyPage(<Nexus />, 'Abrindo o Nexus', 'Sincronizando módulos e sinais do seu sistema.')}</Layout></ProtectedRoute>} />
        <Route path="/app/focus" element={<ProtectedRoute><Layout>{renderLazyPage(<FocusMode />, 'Abrindo o Modo Foco', 'Preparando seus presets de execução para a próxima sessão.')}</Layout></ProtectedRoute>} />
        <Route path="/app/skills" element={<ProtectedRoute>{renderLazyPage(<SkillTree />, 'Abrindo a Skill Tree', 'Carregando perks, requisitos e progresso desbloqueado.')}</ProtectedRoute>} />
        <Route path="/app/logs" element={<ProtectedRoute><Layout>{renderLazyPage(<SystemLogs />, 'Abrindo os Logs', 'Puxando o seu diario reservado e o contexto recente.')}</Layout></ProtectedRoute>} />
        <Route path="/app/oracle" element={<ProtectedRoute><Layout>{renderLazyPage(<Oracle />, 'Abrindo o Oracle', 'Lendo seus sinais para devolver orientação contextual.')}</Layout></ProtectedRoute>} />
        <Route path="/app/pvp" element={<ProtectedRoute><Layout>{renderLazyPage(<PvpDuel />, 'Abrindo a Arena PvP', 'Sincronizando convites, duelos e rivais ativos.')}</Layout></ProtectedRoute>} />
        <Route path="/app/roulette" element={<ProtectedRoute><Layout>{renderLazyPage(<Roulette />, 'Abrindo a Roleta', 'Carregando tickets, histórico e probabilidades.')}</Layout></ProtectedRoute>} />
        <Route path="/app/power-hour" element={<Navigate to="/app/focus?preset=power-hour" replace />} />
        <Route path="/app/challenges/create" element={<ProtectedRoute><Layout>{renderLazyPage(<CreateChallenge />, 'Abrindo o criador de desafios', 'Montando o formulário e as tarefas do desafio.')}</Layout></ProtectedRoute>} />
        <Route path="/app/challenges/:id/edit" element={<ProtectedRoute><Layout>{renderLazyPage(<CreateChallenge />, 'Abrindo editor de desafio', 'Carregando regras, tarefas e configurações do challenge.')}</Layout></ProtectedRoute>} />
        <Route path="/app/challenges/:id" element={<ProtectedRoute><Layout>{renderLazyPage(<ChallengeDetail />, 'Abrindo o desafio', 'Buscando detalhes, tarefas e progresso atual.')}</Layout></ProtectedRoute>} />
        <Route path="/app/challenges" element={<ProtectedRoute><Layout>{renderLazyPage(<Challenges />, 'Abrindo desafios', 'Carregando desafios ativos, encerrados e participações.')}</Layout></ProtectedRoute>} />
        <Route path="/app/ranking" element={<ProtectedRoute><Layout>{renderLazyPage(<Ranking />, 'Abrindo ranking', 'Puxando classificação, sinais sociais e ligas.')}</Layout></ProtectedRoute>} />
        <Route path="/app/shop" element={<ProtectedRoute><Layout>{renderLazyPage(<Shop />, 'Abrindo a loja', 'Sincronizando inventario, itens e efeitos ativos.')}</Layout></ProtectedRoute>} />
        <Route path="/app/friends" element={<ProtectedRoute><Layout>{renderLazyPage(<Friends />, 'Abrindo sua Squad', 'Carregando amigos, pedidos e novos jogadores para conhecer.')}</Layout></ProtectedRoute>} />
        <Route path="/app/messages" element={<ProtectedRoute><Layout>{renderLazyPage(<MessagesInbox />, 'Abrindo mensagens', 'Puxando conversas recentes e alertas não lidos.')}</Layout></ProtectedRoute>} />
        <Route path="/app/notifications" element={<ProtectedRoute><Layout>{renderLazyPage(<Notifications />, 'Abrindo notificações', 'Juntando alertas do app e pendências da sua conta.')}</Layout></ProtectedRoute>} />
        <Route path="/app/chat/:targetUserId" element={<ProtectedRoute><Layout>{renderLazyPage(<DirectChat />, 'Abrindo chat direto', 'Buscando conversa, anexos e histórico recente.')}</Layout></ProtectedRoute>} />
        <Route path="/app/profile/settings" element={<ProtectedRoute><Layout>{renderLazyPage(<Settings />, 'Abrindo configurações', 'Carregando seu perfil, preferências e imagem.')}</Layout></ProtectedRoute>} />
        <Route path="/app/profile/history" element={<ProtectedRoute><Layout>{renderLazyPage(<XPHistory />, 'Abrindo histórico', 'Buscando XP, evidências e progresso registrado.')}</Layout></ProtectedRoute>} />
        <Route path="/app/profile/vip" element={<ProtectedRoute><VipRoute><Layout>{renderLazyPage(<VipThankYou />, 'Abrindo mensagem VIP', 'Carregando o espaco especial reservado para essa pessoa.')}</Layout></VipRoute></ProtectedRoute>} />
        <Route path="/app/profile" element={<ProtectedRoute><Layout>{renderLazyPage(<Profile />, 'Abrindo perfil', 'Puxando aura, classe, badges e sinais pessoais.')}</Layout></ProtectedRoute>} />
        <Route path="/app/users/:id" element={<ProtectedRoute><Layout>{renderLazyPage(<UserProfile />, 'Abrindo perfil publico', 'Buscando sinais sociais e progresso do jogador.')}</Layout></ProtectedRoute>} />
        <Route path="/app/about" element={<ProtectedRoute><Layout>{renderLazyPage(<About />, 'Abrindo sobre', 'Carregando contexto e filosofia do produto.')}</Layout></ProtectedRoute>} />
        <Route path="/app/admin" element={<ProtectedRoute><AdminRoute><Layout>{renderLazyPage(<Admin />, 'Abrindo admin', 'Montando o console e os sinais principais da base.')}</Layout></AdminRoute></ProtectedRoute>} />

        <Route path="/app/*" element={<Navigate to="/app/home" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </HashRouter>
  );
};

export default App;
